import {User} from "konect-model";
import ApiService from "./api-service";
import {Settings} from "../view/Settings";

export default class UserService {
    private static apiService: ApiService = ApiService.getInstance();

    // Store the current user in memory
    private static currentUser: User | null = null;

    // Get the current user
    static getCurrentUser(): User | null {
        return this.currentUser;
    }

    // Set the current user
    static setCurrentUser(user: User | null) {
        this.currentUser = user;
    }

    // Initialize user state from stored authentication
    static async initUserState(): Promise<User | null> {
        // Try to initialize authentication
        const isAuthenticated = await UserService.apiService.initAuth();

        if (isAuthenticated) {
            // If we have authentication but no user in memory, try to get from localStorage
            if (!this.currentUser) {
                try {
                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        this.currentUser = JSON.parse(storedUser);
                    }
                } catch (error) {
                    console.error('Failed to parse stored user:', error);
                }
            }

            // If we still don't have a user, try to fetch from server
            if (!this.currentUser && UserService.apiService.isAuthenticated()) {
                try {
                    // We need to get the username from the JWT token
                    // This is a simplified approach - in a real app, you might decode the JWT
                    // to get the username or use a different endpoint
                    const response = await UserService.apiService.get('/current-user');
                    // Success responses return an object (user); failures return a number (status code)
                    if (response && typeof response !== 'number') {
                        this.currentUser = response as User;
                    }
                } catch (error) {
                    console.error('Failed to fetch current user:', error);
                }
            }
        }

        return this.currentUser;
    }

    static async checkAuthStatus() {
        try {
            const isAuthenticated = UserService.apiService.isAuthenticated();
            if (!isAuthenticated) {
                // Explicit redirect to login, but only if not already on login page
                if (window.location.pathname !== '/login') {
                    //window.location.href = '/login';
                    console.log("Redirect to login");
                }
            }
            return isAuthenticated;
        } catch (error) {
            // Error handling with redirect, but only if not already on login page
            if (window.location.pathname !== '/login') {
                //window.location.href = '/login';
                console.log("Redirect to login")
            }
            return false;
        }
    }

    static isAuthenticated(): boolean {
        return UserService.apiService.isAuthenticated();
    }

    static async loginUser(username: string, password: string) {
        const user = new User();
        user.username = username;
        user.password = password;

        const response = await UserService.apiService.post(`/login`, user);
        if (isNaN(response)) {
            // Store tokens in memory instead of localStorage
            UserService.apiService.setTokens(response.accessToken, response.csrfToken);

            // Store user in memory
            this.setCurrentUser(response.user);

            // Persist only minimal, non-sensitive user info in localStorage
            // (full profile will be fetched when authenticated)
            localStorage.setItem('user', JSON.stringify({ username: response.user.username }));

            return response.user;
        }
        return undefined;
    }

    static async logoutUser(user: User) {
        if (Settings.useOfflineMode) {
            // Clear tokens and user
            UserService.apiService.clearTokens();
            this.setCurrentUser(null);
            localStorage.removeItem('user');
            return new User();
        }

        const response = await UserService.apiService.post(`/logout`, user);
        if (isNaN(response)) {
            // Clear tokens and user
            UserService.apiService.clearTokens();
            this.setCurrentUser(null);
            localStorage.removeItem('user');
            return new User();
        }
        return undefined;
    }

    static async createUser(username: string, password: string, firstName?: string, lastName?: string, email?: string) {
        const user = new User();
        user.username = username;
        user.password = password;
        user.firstName = firstName ? firstName : user.firstName;
        user.lastName = lastName ? lastName : user.lastName;
        user.email = email ? email : user.email;

        let response = await UserService.apiService.post(`/createUser`, user);
        if (isNaN(response)) {
            // Login after successful user creation
            response = await UserService.apiService.post(`/login`, user);
        }

        if (isNaN(response)) {
            // Store tokens in memory instead of localStorage
            UserService.apiService.setTokens(response.accessToken, response.csrfToken);

            // Store user in memory
            this.setCurrentUser(response.user);

            // Persist only minimal, non-sensitive user info in localStorage
            localStorage.setItem('user', JSON.stringify({ username: response.user.username }));

            return response.user;
        }
        return undefined;
    }

    static async getUser(username: string) {
        let response = await UserService.apiService.get(`/readUser/${username}`);
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

    static async updateUser(user: User) {
        let response = await UserService.apiService.patch(`/updateUser`, user);
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

    static async deleteUser(username: string) {
        let response = await UserService.apiService.delete(`/deleteUser/${username}`);
        if (isNaN(response)) {
            // On successful deletion, clear tokens and any stored user state
            UserService.apiService.clearTokens();
            this.setCurrentUser(null);
            localStorage.removeItem('user');
            return true;
        }
        return undefined;
    }

    static async searchUsers(user: User, searchString: string) {
        let response = await UserService.apiService.post(`/searchUsers/${searchString}`, user);
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

    // Password Reset Methods

    static async requestPasswordReset(email: string) {
        const response = await UserService.apiService.post('/requestPasswordReset', { email });
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

    static async validateResetToken(email: string, token: string) {
        const response = await UserService.apiService.post('/validateResetToken', { email, token });
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

    static async resetPassword(email: string, token: string, newPassword: string) {
        const response = await UserService.apiService.post('/resetPassword', { email, token, newPassword });
        if (isNaN(response)) {
            return response;
        }
        return undefined;
    }

}
